import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { n8nService } from '../core/services/n8n.service';
import { geoService } from '../../services/geo.service';
import { teamPreferencesService } from '../../services/teamPreferences.service';
import { queryKeys } from '../../services/query/queryKeys';
import {
  validateSellerUrlField,
  type SellerUrlField,
} from '../modules/CampaignCreator/utils/sellerValidation';
import { parseSellerReference } from '../../utils/sellerReference';
import { GeoSelect } from '../atoms/GeoSelect';
import { Button } from '../atoms/Button';

/**
 * AddSellerModal — the same seller-creation form used in the campaign creator
 * (Guided Form / File Upload), mounted from Team view's "Add seller" button.
 * Creates the seller upstream (add_seller) AND appends `id§Name` to the team
 * pref `seller_ids` registry (single source of truth for sellers).
 */

const DESCRIPTION_MAX_LENGTH = 2500;

interface SellerIdentifiers {
  company_name: string;
  website_url: string;
  linkedin_url: string;
  industry: string;
  country: string;
  country_name: string;
  state: string;
  city: string;
  description: string;
}

const EMPTY_IDENTIFIERS: SellerIdentifiers = {
  company_name: '',
  website_url: '',
  linkedin_url: '',
  industry: '',
  country: 'ES',
  country_name: 'Spain',
  state: '',
  city: '',
  description: '',
};

const inputClass =
  'w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-gray-600 transition-all focus:border-cyan-300/30 focus:outline-none focus:ring-2 focus:ring-[#5260FF]';

export const AddSellerModal: React.FC<{
  teamName: string;
  clientEmail: string;
  existingSellerRefs?: string[];
  onClose: () => void;
  onCreated: (id: string, name: string) => void;
}> = ({ teamName, clientEmail, existingSellerRefs = [], onClose, onCreated }) => {
  const queryClient = useQueryClient();
  const [identifiers, setIdentifiers] = useState<SellerIdentifiers>(EMPTY_IDENTIFIERS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlErrors, setUrlErrors] = useState<Partial<Record<SellerUrlField, string>>>({});

  // Full country list, same cached source as the campaign-creator seller form.
  const { data: countries = [], isLoading: countriesLoading } = useQuery({
    queryKey: ['geo', 'countries'],
    queryFn: () => geoService.listCountries(),
    staleTime: Infinity,
    retry: 1,
  });

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.iso2, label: c.name, emoji: c.emoji })),
    [countries],
  );

  const set = (name: keyof SellerIdentifiers) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setIdentifiers((current) => ({ ...current, [name]: event.target.value }));

  const handleCountryChange = (iso2: string) => {
    const country = countries.find((c) => c.iso2 === iso2);
    setIdentifiers((current) => ({
      ...current,
      country: iso2,
      country_name: country?.name || '',
      state: '',
      city: '',
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const companyName = identifiers.company_name.trim();
    const websiteValidation = validateSellerUrlField('website_url', identifiers.website_url);
    const linkedinValidation = validateSellerUrlField('linkedin_url', identifiers.linkedin_url);

    if (!clientEmail) { setError('Client profile is still loading. Wait a moment and try again.'); return; }
    if (!teamName?.trim()) { setError('Your team is still loading. Wait a moment and try again.'); return; }
    if (!companyName) { setError('Company name is required.'); return; }
    if (websiteValidation.error || linkedinValidation.error) { setError('Review the highlighted URL fields before adding this seller.'); return; }

    // Duplicate guard against the registry.
    const duplicate = existingSellerRefs.some((ref) => {
      const refName = parseSellerReference(ref)?.name || '';
      return refName.toLowerCase() === companyName.toLowerCase();
    });
    if (duplicate) {
      setError(`A seller named "${companyName}" already exists.`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await n8nService.addSeller({
        identifiers: {
          company_name: companyName,
          website_url: websiteValidation.normalizedValue!,
          ...(linkedinValidation.normalizedValue ? { linkedin_url: linkedinValidation.normalizedValue } : {}),
          ...(identifiers.industry.trim() ? { industry: identifiers.industry.trim() } : {}),
          country: identifiers.country,
          country_name: identifiers.country_name,
          ...(identifiers.state.trim() ? { state: identifiers.state.trim() } : {}),
          ...(identifiers.city.trim() ? { city: identifiers.city.trim() } : {}),
          ...(identifiers.description.trim() ? { description: identifiers.description.trim() } : {}),
        },
        client_email: clientEmail,
        team_name: teamName.trim(),
      });
      if (!result.success) throw new Error(result.error || 'Could not create the seller.');

      // Extract the new seller id (the add_seller workflow returns it); fall
      // back to a placeholder the backend resolves by name.
      let createdId: string | null = null;
      const scan = (value: unknown, depth = 0): string | null => {
        if (value === null || value === undefined || depth > 4) return null;
        if (typeof value === 'string' || typeof value === 'number') {
          const text = String(value).trim();
          return /^\d{2,10}$/.test(text) ? text : null;
        }
        if (Array.isArray(value)) {
          for (const entry of [...value].reverse()) {
            const found = scan(entry, depth + 1);
            if (found) return found;
          }
          return null;
        }
        if (typeof value === 'object') {
          for (const key of ['seller_lvl1', 'seller_id', 'lvl1_id', 'id']) {
            const found = scan((value as Record<string, unknown>)[key], depth + 1);
            if (found) return found;
          }
        }
        return null;
      };
      createdId = scan(result.data);
      const ref = `${createdId ?? 'pending'}§${companyName}`;

      // Drop any cached profile for this id (e.g. a previous creation attempt):
      // the profile starts unenriched and must be fetched fresh.
      if (createdId) queryClient.removeQueries({ queryKey: queryKeys.sellers.profile(createdId) });

      // Registry write: append to the team pref `seller_ids`.
      const current = await teamPreferencesService.getPreferences();
      const refs = (current.seller_ids ?? '').split(',').map((r) => r.trim()).filter(Boolean);
      const alreadyPresent = refs.some((r) => r.split('§')[0] === (createdId ?? '') || (parseSellerReference(r)?.name || '').toLowerCase() === companyName.toLowerCase());
      if (!alreadyPresent) {
        await teamPreferencesService.updatePreferences({ seller_ids: [...refs, ref].join(',') });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.preferences.teamSellerIds });
      onCreated(createdId ?? 'pending', companyName);
    } catch (submitError: any) {
      setError(submitError.message || 'Could not create the seller. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10040] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-add-seller-title"
        onClick={(event) => event.stopPropagation()}
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(155deg,rgba(17,18,22,0.99),rgba(7,8,11,0.98))] shadow-[0_34px_110px_rgba(0,0,0,0.62)]"
      >
        <div className="border-b border-white/8 px-6 py-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/18 bg-cyan-400/8 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-100/90">
            <Icon icon="solar:buildings-3-linear" className="h-3.5 w-3.5" aria-hidden="true" />
            Seller identity
          </div>
          <h2 id="team-add-seller-title" className="mt-3 text-xl font-black text-white">Add a seller</h2>
          <p className="mt-1 text-sm text-[#8E95AE]">The seller is registered for your team and available to every campaign.</p>
        </div>

        <form noValidate onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-gray-400">Company name *</span>
                <input value={identifiers.company_name} onChange={set('company_name')} maxLength={160} required placeholder="e.g., Northstar Labs" className={inputClass} />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-gray-400">Website URL *</span>
                <input
                  value={identifiers.website_url}
                  onChange={(event) => setIdentifiers((c) => ({ ...c, website_url: event.target.value }))}
                  onBlur={(event) => {
                    const v = validateSellerUrlField('website_url', event.currentTarget.value);
                    setUrlErrors((c) => ({ ...c, website_url: v.error }));
                    if (v.normalizedValue !== undefined) setIdentifiers((c) => ({ ...c, website_url: v.normalizedValue }));
                  }}
                  inputMode="url" required placeholder="northstarlabs.com"
                  className={inputClass}
                />
                {urlErrors.website_url && <span role="alert" className="mt-1 block text-xs text-rose-300">{urlErrors.website_url}</span>}
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-gray-400">LinkedIn company URL</span>
                <input
                  value={identifiers.linkedin_url}
                  onChange={(event) => setIdentifiers((c) => ({ ...c, linkedin_url: event.target.value }))}
                  onBlur={(event) => {
                    const v = validateSellerUrlField('linkedin_url', event.currentTarget.value);
                    setUrlErrors((c) => ({ ...c, linkedin_url: v.error }));
                    if (v.normalizedValue !== undefined) setIdentifiers((c) => ({ ...c, linkedin_url: v.normalizedValue }));
                  }}
                  inputMode="url" placeholder="linkedin.com/company/northstar-labs"
                  className={inputClass}
                />
                {urlErrors.linkedin_url && <span role="alert" className="mt-1 block text-xs text-rose-300">{urlErrors.linkedin_url}</span>}
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-gray-400">Industry</span>
                <input value={identifiers.industry} onChange={set('industry')} maxLength={120} placeholder="e.g., B2B SaaS" className={inputClass} />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <GeoSelect
                id="team-seller-country"
                label="Country *"
                value={identifiers.country}
                onChange={handleCountryChange}
                options={countryOptions}
                placeholder="Select a country…"
                loading={countriesLoading}
              />
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-gray-400">State / Province</span>
                <input value={identifiers.state} onChange={set('state')} className={inputClass} placeholder="Optional" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-gray-400">City</span>
                <input value={identifiers.city} onChange={set('city')} className={inputClass} placeholder="Optional" />
              </label>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold text-gray-400">Description</span>
              <textarea
                value={identifiers.description}
                onChange={(event) => setIdentifiers((c) => ({ ...c, description: event.target.value.slice(0, DESCRIPTION_MAX_LENGTH) }))}
                rows={3}
                maxLength={DESCRIPTION_MAX_LENGTH}
                placeholder="Brief context about the seller company…"
                className={`${inputClass} resize-y`}
              />
            </label>

            {error && (
              <p role="alert" className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">{error}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-white/8 px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              icon={<Icon icon="bx:plus" className="h-4 w-4" aria-hidden="true" />}
            >
              {isSubmitting ? 'Adding seller…' : 'Add seller'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};
